import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Container,
  Card,
  CardContent,
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Stack
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useHistory } from 'react-router-dom';
import companyService from '../../services/companyService';
import { getCurrentUser } from '../../redux/slices/authSlice';
import { validators } from '../../utils/validators';
import alertify from 'alertifyjs';
import { useHasPermission } from '../../hooks/usePermission';
import ForbiddenPage from '../ForbiddenPage';

/**
 * CompanySettingsPage - Company information management
 * 
 * Features:
 * - View/Edit company info (name, taxNumber, phone, address, website)
 * - Company owner only
 * - Real-time validation
 * - Success/Error feedback
 * 
 * Access: company_owner only
 * Route: /company/settings
 */
const CompanySettingsPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { user } = useSelector((state) => state.auth);
  
  // Permission-based guard (replaces userType guard)
  const hasPermission = useHasPermission('companies.view');

  // State
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [companyForm, setCompanyForm] = useState({
    companyName: '',
    taxNumber: '',
    phone: '',
    address: '',
    website: ''
  });
  const [errors, setErrors] = useState({});

  // Fetch company info on mount
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      if (!user?.companyId?._id) {
        alertify.error('Şirket bilgisi bulunamadı');
        setFetchLoading(false);
        return;
      }

      try {
        setFetchLoading(true);
        const { company } = await companyService.getCompany(user.companyId._id);
        
        setCompanyForm({
          companyName: company.companyName || '',
          taxNumber: company.taxNumber || '',
          phone: company.phone || '',
          address: company.address || '',
          website: company.website || ''
        });
      } catch (error) {
        alertify.error(error.response?.data?.message || 'Şirket bilgileri yüklenemedi');
      } finally {
        setFetchLoading(false);
      }
    };

    fetchCompanyInfo();
  }, [user]);

  // Validation
  const validate = () => {
    const newErrors = {};

    // Company Name - Required
    const companyNameError = validators.required(companyForm.companyName, 'Şirket Unvanı');
    if (companyNameError) newErrors.companyName = companyNameError;

    // Tax Number - Required, 10 digits
    const taxNumberError = validators.required(companyForm.taxNumber, 'Vergi Numarası');
    if (taxNumberError) {
      newErrors.taxNumber = taxNumberError;
    } else {
      // Check if it's only digits
      if (!/^\d+$/.test(companyForm.taxNumber)) {
        newErrors.taxNumber = 'Vergi numarası sadece rakamlardan oluşmalıdır';
      } else if (companyForm.taxNumber.length !== 10) {
        newErrors.taxNumber = 'Vergi numarası 10 haneli olmalıdır';
      }
    }

    // Phone - Optional, but if provided must be valid format
    if (companyForm.phone && companyForm.phone.trim()) {
      // Remove all non-digit characters for validation
      const phoneDigits = companyForm.phone.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        newErrors.phone = 'Telefon numarası en az 10 haneli olmalıdır';
      } else if (phoneDigits.length > 11) {
        newErrors.phone = 'Telefon numarası en fazla 11 haneli olmalıdır';
      }
    }

    // Website - Optional, but if provided must be valid URL
    if (companyForm.website && companyForm.website.trim()) {
      const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
      if (!urlPattern.test(companyForm.website)) {
        newErrors.website = 'Geçerli bir website adresi giriniz (örn: https://www.ornek.com)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handlers
  const handleEdit = () => {
    setIsEditMode(true);
    setErrors({});
  };

  const handleCancel = () => {
    setIsEditMode(false);
    // Reset form to original values
    if (user?.companyId) {
      setCompanyForm({
        companyName: user.companyId.companyName || '',
        taxNumber: user.companyId.taxNumber || '',
        phone: user.companyId.phone || '',
        address: user.companyId.address || '',
        website: user.companyId.website || ''
      });
    }
    setErrors({});
  };

  const handleSave = async () => {
    if (!validate()) {
      alertify.error('Lütfen form hatalarını düzeltin');
      return;
    }

    setLoading(true);
    try {
      await companyService.updateCompany(user.companyId._id, companyForm);

      // Refresh user data to get updated company info
      await dispatch(getCurrentUser()).unwrap();

      alertify.success('Şirket bilgileri başarıyla güncellendi');
      setIsEditMode(false);
    } catch (error) {
      // Backend validation errors
      const errorMessage = error.response?.data?.message || 'Şirket bilgileri güncellenemedi';
      const backendErrors = error.response?.data?.errors;
      
      if (backendErrors && Array.isArray(backendErrors)) {
        // Show all backend errors
        backendErrors.forEach(err => alertify.error(err));
      } else {
        alertify.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Format phone number as user types: +90 (5XX) XXX XX XX
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format based on length
    if (digits.length === 0) return '';
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    if (digits.length <= 8) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    if (digits.length <= 10) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8)}`;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
  };

  const handleChange = (field) => (e) => {
    let value = e.target.value;

    // Real-time validation for tax number (only digits, max 10)
    if (field === 'taxNumber') {
      value = value.replace(/\D/g, ''); // Remove non-digits
      if (value.length > 10) return; // Max 10 digits - block input
    }

    // Real-time validation for phone (format as user types)
    if (field === 'phone') {
      const digits = value.replace(/\D/g, '');
      if (digits.length > 11) return; // Max 11 digits - block input
      value = formatPhoneNumber(value);
    }

    setCompanyForm({
      ...companyForm,
      [field]: value
    });

    // Clear error for this field
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: null
      });
    }
  };

  // Permission-based guard (replaces userType guard)
  if (!hasPermission) {
    return <ForbiddenPage />;
  }

  // Loading state
  if (fetchLoading) {
    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ mt: 2 }} color="text.secondary">
              Şirket bilgileri yükleniyor...
            </Typography>
          </Box>
        </Container>
    );
  }

  return (
      <Container maxWidth="xl">
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}
          sx={{ mb: 3 }}
        >
          Geri Dön
        </Button>

        {/* Header */}
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Şirket Ayarları
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Şirket bilgilerinizi görüntüleyin ve düzenleyin
          </Typography>
        </Box>

        {/* Content Card */}
        <Card>
          <CardContent sx={{ p: 4 }}>
            {!isEditMode ? (
              // VIEW MODE
              <Stack spacing={3} sx={{ maxWidth: 800 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, p: 3, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <BusinessIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" fontWeight="bold">
                      {companyForm.companyName || '-'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Vergi No: {companyForm.taxNumber || '-'}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleEdit}
                  >
                    Düzenle
                  </Button>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  <Box sx={{ flex: '1 1 250px' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      Telefon
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {companyForm.phone || '-'}
                    </Typography>
                  </Box>

                  <Box sx={{ flex: '1 1 250px' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      Website
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {companyForm.website || '-'}
                    </Typography>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    Adres
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {companyForm.address || '-'}
                  </Typography>
                </Box>
              </Stack>
            ) : (
              // EDIT MODE
              <Stack spacing={3} sx={{ maxWidth: 800 }}>
                <Alert severity="info" icon={<BusinessIcon />}>
                  Şirket bilgilerini düzenlerken dikkatli olun. Bu bilgiler tüm şirket kullanıcıları tarafından görülebilir.
                </Alert>

                <TextField
                  fullWidth
                  label="Şirket Unvanı *"
                  value={companyForm.companyName}
                  onChange={handleChange('companyName')}
                  error={!!errors.companyName}
                  helperText={errors.companyName}
                  disabled={loading}
                  autoFocus
                />

                <TextField
                  fullWidth
                  label="Vergi Numarası *"
                  value={companyForm.taxNumber}
                  onChange={handleChange('taxNumber')}
                  error={!!errors.taxNumber}
                  helperText={errors.taxNumber || '10 haneli vergi numaranız'}
                  disabled={loading}
                  inputProps={{ maxLength: 10 }}
                />

                <TextField
                  fullWidth
                  label="Telefon"
                  value={companyForm.phone}
                  onChange={handleChange('phone')}
                  error={!!errors.phone}
                  helperText={errors.phone || 'Örnek: 555 555 55 55'}
                  placeholder="5XX XXX XX XX"
                  disabled={loading}
                />

                <TextField
                  fullWidth
                  label="Adres"
                  value={companyForm.address}
                  onChange={handleChange('address')}
                  error={!!errors.address}
                  helperText={errors.address}
                  multiline
                  rows={3}
                  disabled={loading}
                />

                <TextField
                  fullWidth
                  label="Website"
                  value={companyForm.website}
                  onChange={handleChange('website')}
                  error={!!errors.website}
                  helperText={errors.website || 'https:// ile başlayan URL giriniz'}
                  placeholder="https://www.ornek.com"
                  disabled={loading}
                />

                <Box sx={{ display: 'flex', gap: 2, pt: 3 }}>
                  <Button
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={loading}
                    size="large"
                  >
                    {loading ? 'Kaydediliyor...' : 'Kaydet'}
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                    disabled={loading}
                    size="large"
                  >
                    İptal
                  </Button>
                </Box>
              </Stack>
            )}
          </CardContent>
        </Card>
      </Container>
  );
};

export default CompanySettingsPage;

